/**
 * usePlacementGuards — the two protections legacy `ValidationController` ran
 * while an order was in flight, ported natively.
 *
 *   1. TAB is swallowed, so a keypress cannot move focus into the Stripe card
 *      fields between the confirm call and the redirect. The native payment box
 *      does not disable its `PaymentElement` while placing, so this is the only
 *      thing standing between a stray keystroke and an edit mid-confirm.
 *   2. Navigating away mid-placement warns, via the platform toast rather than
 *      legacy's `window.alert`.
 *
 * The warning is checked one second AFTER unmount, against the live store: a
 * successful placement navigates away and clears `isPlacingOrder` in the same
 * tick, so the toast never fires on the happy path. Legacy achieved this with a
 * timeout plus a second effect cancelling it on `orderHasBeenPlaced`; reading the
 * flag at fire time says the same thing in one place.
 */

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { KeyboardEventKey } from 'constants/misc'
import { useSnackbar } from 'components/contexts/SnackbarService.context'

import { usePfStore } from '../../store'

/**
 * Registers both placement guards for the lifetime of the calling component.
 *
 * @example
 * export const Summary: React.FC = () => {
 *   usePlacementGuards()
 *   // …
 * }
 */
export const usePlacementGuards = (): void => {
  const { t } = useTranslation(['order'])
  const { spawnErrorToast } = useSnackbar()
  const isPlacingOrder = usePfStore((s) => s.isPlacingOrder)

  useEffect(() => {
    if (!isPlacingOrder) return

    const swallowTab = (e: KeyboardEvent) => {
      if (e.key !== KeyboardEventKey.TAB) return
      e.preventDefault()
      e.stopPropagation()
    }

    window.addEventListener('keydown', swallowTab)

    return () => {
      window.removeEventListener('keydown', swallowTab)

      window.setTimeout(() => {
        if (usePfStore.getState().isPlacingOrder) {
          spawnErrorToast(t('order:step_validation.leave_warning'))
        }
      }, 1000)
    }
  }, [isPlacingOrder, spawnErrorToast, t])
}
